/**
 * `blog` — a portal-hosted blog's posts, revisions, authors and media, and the delivery
 * API a client's site reads them through (`.docs/46-blog-and-forms.md` §4).
 *
 * Two audiences, two families of shape:
 *
 *   - **the workspace** — the editor's view of a post (its working copy, what is live,
 *     whether the two differ, the checklist), a list row, revisions, the author
 *     directory, media, and every input. The client's REST routes and the `blog_*`
 *     actions answer the same objects, because the SPA mounts one workspace over both
 *     (decision 12);
 *   - **delivery** — `PostSummary` and `PostFull`, what a static site pulls at build
 *     time with the publishable key, in snake case like every wire here (decision 13).
 *
 * The body is a ProseMirror document validated by `@fareast/rich-text` `parseBody`, which
 * this package cannot import (it imports nothing internal), so here it is an object of
 * unknown keys and the API is what refuses one that is not a document.
 */
import { z } from 'zod';
import { SLUG_MAX_LENGTH, isSlug, slugify } from '../slug.js';
import { isPlainLine } from '../text.js';
import { blogPostMachine } from '../states.js';
import { DateOnlySchema, Rfc3339Schema } from './primitives.js';
import { PostRefSchema, WidgetRefSchema } from './refs.js';
/* -------------------------------------------------------------------- the limits */
/** The longest title a working copy holds. Publishing has no lower bar than "not empty". */
export const BLOG_TITLE_MAX = 200;
/**
 * The longest SEO title or description a **working copy** holds — generous on purpose,
 * because a draft is somewhere to think. Publishing is held to the search-result
 * lengths (`@fareast/rich-text` `SEO_TITLE_MAX`, `DESCRIPTION_MAX`), and the checklist
 * says so before the refusal does.
 */
export const BLOG_SEO_TITLE_MAX = 200;
export const BLOG_DESCRIPTION_MAX = 1_000;
/** Tags on one post, and the longest one. */
export const BLOG_TAGS_MAX = 20;
export const BLOG_TAG_MAX = 40;
/** The longest alt text for a cover: the body's own image-attribute bound. */
export const BLOG_ALT_MAX = 300;
/** The longest canonical URL. */
export const BLOG_URL_MAX = 2_048;
/** An author's key — a slug, `founder` — and the longest name. */
export const BLOG_AUTHOR_KEY_MAX = 40;
export const BLOG_AUTHOR_NAME_MAX = 120;
/** The largest image a blog takes: 10 MB, measured as it arrives. */
export const BLOG_MEDIA_MAX_BYTES = 10 * 1024 * 1024;
/**
 * The image types a blog stores, by what the bytes are (sniffed, never the header's
 * word for it). **Never SVG**: an SVG is a document that can carry script, and these
 * files are served from the portal's own origin.
 */
export const BLOG_MEDIA_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/avif',
];
/** Where a post lives on the site when the widget's config does not say. */
export const BLOG_DEFAULT_POST_PATH = '/blog/';
/** How many live posts one delivery page holds by default, and at most. */
export const BLOG_DELIVERY_PAGE_DEFAULT = 20;
export const BLOG_DELIVERY_PAGE_MAX = 100;
/* ---------------------------------------------------------------- the vocabulary */
export const BlogPostStatusSchema = z
    .enum(blogPostMachine.states)
    .describe('The stored state: draft, published (live, or scheduled when its date is still to ' +
    'come), unpublished, or archived. phase is the one to show a person.');
/**
 * What a person is told a post is, derived from `status` and the clock: a `published`
 * post whose `published_at` is still to come is **scheduled** (decision 8 — it is not
 * stored, so it becomes live on the second with no write in between).
 */
export const BLOG_POST_PHASES = [
    'draft',
    'scheduled',
    'published',
    'unpublished',
    'archived',
];
export const BlogPostPhaseSchema = z
    .enum(BLOG_POST_PHASES)
    .describe('Where the post stands now: draft, scheduled (published with a date still to come), ' +
    'published (live on the site), unpublished, or archived.');
/** The phase of a post with this status and public date, at this instant. */
export function blogPostPhaseOf(status, publishedAt, now) {
    if (status !== 'published')
        return status;
    return publishedAt !== null && publishedAt.getTime() > now.getTime() ? 'scheduled' : 'published';
}
/**
 * Why a revision was written. `publish`, `update` and `schedule` are what a publish
 * does to a draft, to a live post and for a date to come; `autosave` is an editing
 * session's snapshot (the newest revision older than ten minutes, and the content
 * changed — Ghost's rule); `restore` is a revision copied back into the working copy.
 */
export const BLOG_REVISION_REASONS = [
    'publish',
    'update',
    'schedule',
    'autosave',
    'restore',
];
export const BlogRevisionReasonSchema = z
    .enum(BLOG_REVISION_REASONS)
    .describe('Why the revision exists: publish, update (a live post changed), schedule, autosave ' +
    '(an editing session), or restore (an older revision copied back).');
/* ------------------------------------------------------------------ the fields */
export const BlogSlugSchema = z
    .string()
    .refine(isSlug, `A slug is lower-case letters and digits in words joined by single hyphens, at most ${String(SLUG_MAX_LENGTH)} characters.`)
    .describe('The post’s public address on the site, after the post path — "kubernetes-cost-review". ' +
    'Lower-case words joined by hyphens. Every address a post has had keeps redirecting ' +
    'to it, and no other post can ever take one.');
/**
 * One line of a post's plain text — a title, a description, an alt text, a tag, an
 * author's name: no control character but tab, no line break, no unpaired surrogate
 * (`text.ts`, the one definition). Postgres refuses a NUL, so without this one was a 500.
 */
const PLAIN_LINE = [
    isPlainLine,
    'This holds a line break or a control character; it must be one line of plain text.',
];
export const BlogTagNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(BLOG_TAG_MAX)
    .refine(...PLAIN_LINE)
    .refine((name) => slugify(name) !== '', 'A tag needs at least one letter or digit.')
    .describe('A tag as a reader sees it — "Cloud costs". Its slug is made from it.');
export const BlogAuthorKeySchema = z
    .string()
    .max(BLOG_AUTHOR_KEY_MAX)
    .refine(isSlug, 'An author key is a slug: lower-case words joined by hyphens, "founder".')
    .describe('The author, by their key in the blog’s author directory — "founder". A site maps its ' +
    'structured-data Person on it.');
/** A blog upload's public id: 22 base64url characters from 16 random bytes. */
export const BLOG_MEDIA_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;
export const BlogMediaIdSchema = z
    .string()
    .regex(BLOG_MEDIA_ID_PATTERN, 'A media id is 22 characters of letters, digits, - and _.')
    .describe('An uploaded image, by the id blog media answers with — 22 characters.');
const HttpsUrlSchema = z
    .string()
    .trim()
    .max(BLOG_URL_MAX)
    .pipe(z.url({
    protocol: /^https$/,
    hostname: z.regexes.domain,
    error: 'An https:// address with a host name.',
}));
/**
 * A post's document. An object here, because a recursive document is not a shape a tool
 * schema can carry; the API runs `@fareast/rich-text` `parseBody` over it and refuses
 * anything that is not a document of the blog's schema, every reason listed.
 */
export const BlogBodySchema = z
    .record(z.string(), z.unknown())
    .describe('The body, as a TipTap/ProseMirror document: { "type": "doc", "content": [...] } over ' +
    'the blog schema — paragraphs, headings 2 to 4, lists, quotes, code, tables, images. ' +
    'Validated by structure on save; a node or attribute the schema does not name is refused ' +
    'with its path.');
/**
 * The working copy's content, every field. A save sends all of it — the editor holds the
 * whole post — so a field left out is a mistake rather than "leave it alone".
 */
const BLOG_CONTENT_FIELDS = {
    title: z
        .string()
        .trim()
        .max(BLOG_TITLE_MAX)
        .refine(...PLAIN_LINE)
        .describe('The title: the page’s heading. May be empty while drafting; publishing needs one.'),
    seo_title: z
        .string()
        .trim()
        .max(BLOG_SEO_TITLE_MAX)
        .refine(...PLAIN_LINE)
        .nullable()
        .describe('The title search results show, when it should differ from the heading. Publishing ' +
        'refuses one over 60 characters. Null to use the title.'),
    description: z
        .string()
        .trim()
        .max(BLOG_DESCRIPTION_MAX)
        .refine(...PLAIN_LINE)
        .nullable()
        .describe('The meta description search results and link previews show. Publishing needs one, ' +
        'at most 155 characters; under 50 is allowed and warned about.'),
    body: BlogBodySchema,
    tags: z
        .array(BlogTagNameSchema)
        .max(BLOG_TAGS_MAX)
        .refine((tags) => new Set(tags.map((tag) => slugify(tag))).size === tags.length, 'Name each tag once — two tags whose slugs are the same are the same tag.')
        .describe(`The post’s tags, at most ${String(BLOG_TAGS_MAX)}, each named once.`),
    cover_media_id: BlogMediaIdSchema.nullable().describe('The cover image, by its media id — one of this blog’s uploads. Null for none.'),
    cover_alt: z
        .string()
        .trim()
        .max(BLOG_ALT_MAX)
        .refine(...PLAIN_LINE)
        .nullable()
        .describe('The cover’s alt text. Publishing needs it when there is a cover.'),
    author_key: BlogAuthorKeySchema.nullable().describe('The author, by key in the blog’s author directory. Null for none.'),
    canonical_url: HttpsUrlSchema.nullable().describe('Where the original of this post lives, when it is a copy — an https:// URL. Null when ' +
        'the post is its own original.'),
    noindex: z
        .boolean()
        .describe('True to ask search engines not to index the post. The site renders it.'),
    reviewed_on: DateOnlySchema.nullable().describe('The day the post was last reviewed for accuracy, YYYY-MM-DD. Null to use the day it ' +
        'was published.'),
};
/* ---------------------------------------------------------------- media, authors */
export const BlogMediaSchema = z
    .object({
    id: BlogMediaIdSchema,
    url: z
        .string()
        .describe('Where the image is served, on the portal: /api/v1/blog/media/<id>/<file name>. Put ' +
        'it in an image’s src; the delivery API makes it absolute for a site.'),
    file_name: z.string().describe('The file name it is served under.'),
    mime: z.enum(BLOG_MEDIA_TYPES).describe('What the bytes are, as sniffed.'),
    bytes: z.int().describe('Its size in bytes.'),
    width: z.int().describe('Its width in pixels.'),
    height: z.int().describe('Its height in pixels.'),
    created_at: Rfc3339Schema.describe('When it was uploaded.'),
})
    .describe('One uploaded image. Never deleted, so its address never stops working.');
export const BlogAuthorSchema = z
    .object({
    key: BlogAuthorKeySchema,
    name: z.string().describe('The name a reader sees.'),
    url: z.string().nullable().describe('Their page — an https:// URL — or null.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('One entry in the blog’s author directory.');
/* --------------------------------------------------------------- the editor view */
export const BlogCheckSchema = z
    .object({
    code: z
        .string()
        .describe('A stable code — title_missing, description_too_long, unfinished_marker, ' +
        'cover_missing, … — the same one the editor’s checklist keys on.'),
    field: z.string().describe('The working-copy field it is about: title, body, tags, …'),
    message: z.string().describe('What is wrong, in one sentence.'),
})
    .describe('One pre-publish check the working copy fails.');
export const BlogChecksSchema = z
    .object({
    blocking: z
        .array(BlogCheckSchema)
        .describe('What stops a publish. Empty means the post may go out.'),
    warnings: z.array(BlogCheckSchema).describe('What is worth fixing and does not stop one.'),
})
    .describe('The pre-publish checklist, over the working copy as it is now.');
export const BlogLiveSchema = z
    .object({
    revision: z.int().describe('The revision the site is served, by number.'),
    title: z.string().describe('Its title.'),
    slug: z.string().describe('The address it went out under.'),
    published_at: Rfc3339Schema.describe('Its public date; in the future while scheduled.'),
    content_updated_at: Rfc3339Schema.describe('When the live content last changed.'),
})
    .describe('What the site is served, or will be at published_at.');
/** Who saved the working copy last, and when — what a conflict names. */
const SAVED_BY = z
    .string()
    .nullable()
    .describe('Who saved it last — a person’s or an agent’s name — or null for the system.');
export const BlogPostSchema = z
    .object({
    ref: PostRefSchema,
    widget: WidgetRefSchema,
    status: BlogPostStatusSchema,
    phase: BlogPostPhaseSchema,
    slug: BlogSlugSchema,
    previous_slugs: z
        .array(z.string())
        .describe('Every address the post had before this one, newest first. Each still redirects.'),
    url: z
        .string()
        .nullable()
        .describe('Its address on the site, or null until the studio sets the site’s address.'),
    ...BLOG_CONTENT_FIELDS,
    title: z.string().describe('The title.'),
    seo_title: z.string().nullable().describe('The SEO title, or null for the title.'),
    description: z.string().nullable().describe('The meta description, or null.'),
    tags: z.array(z.string()).describe('The tags.'),
    cover_alt: z.string().nullable().describe('The cover’s alt text, or null.'),
    canonical_url: z.string().nullable().describe('The canonical URL, or null.'),
    body_format: z.string().describe('The body’s format and schema version: tiptap/1.'),
    cover: BlogMediaSchema.nullable().describe('The cover image, or null.'),
    draft_version: z
        .int()
        .describe('The working copy’s version. Send it back with the next save; a save made from an ' +
        'older one is refused with 409 rather than overwriting somebody else’s.'),
    draft_saved_at: Rfc3339Schema.describe('When the working copy was last saved.'),
    draft_saved_by: SAVED_BY,
    has_unpublished_changes: z
        .boolean()
        .describe('True when the working copy differs from what is published or scheduled.'),
    checks: BlogChecksSchema,
    live: BlogLiveSchema.nullable().describe('What is published or scheduled, or null when nothing is.'),
    published_at: Rfc3339Schema.nullable().describe('The public date — in the future while scheduled — or null if never published.'),
    first_published_at: Rfc3339Schema.nullable().describe('When it first went out, or null.'),
    content_updated_at: Rfc3339Schema.nullable().describe('When the published content last changed — the site’s "updated" date — or null.'),
    unpublished_at: Rfc3339Schema.nullable().describe('When it was last taken down, or null.'),
    word_count: z.int().describe('Words in the working copy’s body.'),
    reading_minutes: z.int().describe('Minutes to read the working copy, at least 1.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('A post as the editor sees it: the working copy, what is live, and the checklist.');
export const BlogPostListItemSchema = z
    .object({
    ref: PostRefSchema,
    slug: z.string().describe('Its current address.'),
    title: z.string().describe('The working copy’s title.'),
    status: BlogPostStatusSchema,
    phase: BlogPostPhaseSchema,
    tags: z.array(z.string()).describe('The working copy’s tags.'),
    author_key: z.string().nullable().describe('The author’s key, or null.'),
    url: z.string().nullable().describe('Its address on the site, or null.'),
    has_unpublished_changes: z
        .boolean()
        .describe('True when the working copy differs from what is published or scheduled.'),
    published_at: Rfc3339Schema.nullable().describe('The public date, or null.'),
    draft_saved_at: Rfc3339Schema.describe('When the working copy was last saved.'),
    draft_saved_by: SAVED_BY,
    created_at: Rfc3339Schema,
})
    .describe('One post in the workspace’s list.');
export const BlogPostPageSchema = z
    .object({
    posts: z.array(BlogPostListItemSchema).describe('This page, most recently saved first.'),
    next_cursor: z
        .string()
        .nullable()
        .describe('Pass as cursor for the next page, or null when this was the last.'),
})
    .describe('A page of the blog’s posts.');
export const BlogRevisionSummarySchema = z
    .object({
    number: z.int().describe('The revision’s number, from 1, per post.'),
    reason: BlogRevisionReasonSchema,
    title: z.string().describe('The title as it was.'),
    slug: z.string().describe('The address as it was.'),
    created_at: Rfc3339Schema.describe('When it was written.'),
    created_by: SAVED_BY,
    live: z.boolean().describe('True for the revision the site is served (or scheduled to be).'),
})
    .describe('One revision in a post’s history.');
export const BlogRevisionSchema = BlogRevisionSummarySchema.extend({
    seo_title: z.string().nullable().describe('The SEO title as it was.'),
    description: z.string().nullable().describe('The description as it was.'),
    body: BlogBodySchema.describe('The body as it was.'),
    body_format: z.string().describe('The body’s format: tiptap/1.'),
    tags: z.array(z.string()).describe('The tags as they were.'),
    cover_media_id: z.string().nullable().describe('The cover’s media id as it was, or null.'),
    cover_alt: z.string().nullable().describe('The cover’s alt text as it was.'),
    author_key: z.string().nullable().describe('The author’s key as it was.'),
    canonical_url: z.string().nullable().describe('The canonical URL as it was.'),
    noindex: z.boolean().describe('Whether search engines were asked not to index it.'),
    reviewed_on: DateOnlySchema.nullable().describe('The last-reviewed day as it was.'),
}).describe('One revision, whole: everything the post said at that moment.');
/* -------------------------------------------------------------------- the inputs */
export const CreateBlogPostSchema = z
    .strictObject({
    title: z
        .string()
        .trim()
        .max(BLOG_TITLE_MAX)
        .default('')
        .describe('The title to start from. May be empty; the slug is made from it.'),
    slug: BlogSlugSchema.optional().describe('The address, when it should not be made from the title. Refused if any post has ever ' +
        'had it.'),
})
    .describe('A new draft.');
export const SaveBlogDraftSchema = z
    .strictObject({
    draft_version: z
        .int()
        .min(1)
        .describe('The draft_version this save was made from. A save from an older one is refused with ' +
        '409 and the current version, so nobody’s work is overwritten unseen.'),
    ...BLOG_CONTENT_FIELDS,
})
    .describe('The whole working copy, as the editor holds it.');
export const RestoreBlogRevisionSchema = z
    .strictObject({
    draft_version: z
        .int()
        .min(1)
        .describe('The draft_version of the working copy this restore replaces. An older one is refused ' +
        'with 409 and the current version, as a save is, so a newer copy is never replaced ' +
        'unseen.'),
})
    .describe('Restoring a revision into the working copy, from the version it replaces.');
export const SetBlogSlugSchema = z
    .strictObject({ slug: BlogSlugSchema })
    .describe('A new address for the post.');
export const PublishBlogPostSchema = z
    .strictObject({
    publish_at: Rfc3339Schema.optional().describe('When it goes live. Left out, or in the past, it goes live now; in the future, it is ' +
        'scheduled and goes live on its own at that instant.'),
})
    .describe('Publishing, now or at a time.');
export const BlogAuthorUrlSchema = HttpsUrlSchema.describe('Their page, an https:// URL — a profile, an about page.');
export const AddBlogAuthorSchema = z
    .strictObject({
    key: BlogAuthorKeySchema,
    name: z
        .string()
        .trim()
        .min(1)
        .max(BLOG_AUTHOR_NAME_MAX)
        .refine(...PLAIN_LINE)
        .describe('The name a reader sees.'),
    url: BlogAuthorUrlSchema.nullable().default(null),
})
    .describe('A new entry in the author directory.');
export const UpdateBlogAuthorSchema = z
    .strictObject({
    name: z
        .string()
        .trim()
        .min(1)
        .max(BLOG_AUTHOR_NAME_MAX)
        .optional()
        .describe('A new name. It reaches every post by this author at once.'),
    url: BlogAuthorUrlSchema.nullable().optional().describe('A new page, or null to clear it.'),
})
    .describe('A change to an author. What is left out is left alone; the key never changes.');
/** The workspace list's filters. */
export const BlogPostsQuerySchema = z.strictObject({
    phase: BlogPostPhaseSchema.optional().describe('Only posts in this phase. Left out, all.'),
    q: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .optional()
        .describe('Only posts whose title contains this, in any case.'),
});
/* ---------------------------------------------------------------- the delivery API */
export const PostTagSchema = z
    .object({
    name: z.string().describe('The tag as written.'),
    slug: z.string().describe('Its slug, for a tag page’s address.'),
})
    .describe('One tag.');
export const PostCoverSchema = z
    .object({
    url: z.string().describe('The image’s absolute URL, served by the portal.'),
    width: z.int().describe('Width in pixels.'),
    height: z.int().describe('Height in pixels.'),
    alt: z.string().describe('Its alt text.'),
    mime: z.string().describe('Its type.'),
})
    .describe('A post’s cover image.');
export const PostAuthorSchema = z
    .object({
    key: z.string().describe('The author’s key — what a site maps its Person on.'),
    name: z.string().describe('The name, as the directory has it now.'),
    url: z.string().nullable().describe('Their page, or null.'),
})
    .describe('A post’s author.');
export const PostSummarySchema = z
    .object({
    ref: PostRefSchema,
    slug: z.string().describe('The current address.'),
    title: z.string(),
    seo_title: z.string().nullable(),
    description: z.string().nullable(),
    tags: z.array(PostTagSchema),
    cover: PostCoverSchema.nullable(),
    author: PostAuthorSchema.nullable(),
    published_at: Rfc3339Schema.nullable().describe('The public date. Null only on a preview of a post never published.'),
    updated_at: Rfc3339Schema.nullable().describe('When the published content last changed.'),
    reviewed_on: DateOnlySchema.nullable().describe('The day it was last reviewed, or the day it was published.'),
    reading_minutes: z.int(),
    word_count: z.int(),
    canonical_url: z.string().nullable(),
    noindex: z.boolean(),
    url: z.string().nullable().describe('Its address on the site, or null.'),
})
    .describe('A live post, without its body.');
export const PostHeadingSchema = z
    .object({
    level: z.int().describe('2, 3 or 4.'),
    text: z.string(),
    id: z.string().describe('The id the HTML puts on it, for a table of contents.'),
})
    .describe('One heading in the body.');
export const PostFullSchema = PostSummarySchema.extend({
    html: z.string().describe('The body as HTML.'),
    markdown: z.string().describe('The body as GitHub-flavoured Markdown.'),
    doc: z.record(z.string(), z.unknown()).describe('The body as its document JSON.'),
    body_format: z.string().describe('The document’s format: tiptap/1.'),
    headings: z.array(PostHeadingSchema),
    previous_slugs: z.array(z.string()).describe('Every address it had before, newest first.'),
}).describe('A live post, whole.');
export const PostsPageSchema = z
    .object({
    posts: z.array(PostSummarySchema),
    next_cursor: z.string().nullable(),
})
    .describe('A page of live posts, newest first.');
export const BlogTagCountSchema = z
    .object({ name: z.string(), slug: z.string(), count: z.int() })
    .describe('A tag and how many live posts carry it.');
export const DeliveryPostsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(BLOG_DELIVERY_PAGE_MAX)
        .default(BLOG_DELIVERY_PAGE_DEFAULT),
    cursor: z.string().min(1).max(500).optional(),
    tag: z.string().min(1).max(SLUG_MAX_LENGTH).optional(),
});
//# sourceMappingURL=blog.js.map